<script setup lang="ts">
import { useAuthStore } from "@/stores/auth";
definePageMeta({ layout: "auth" });

const email = ref("");
const password = ref("");
const name = ref("");
const authStore = useAuthStore();

const error = ref("");
const pwdVisibility = ref(false);
const togglePwdVisibility = () => {
  // console.log("Toggling password visibility", pwdVisibility.value);
  pwdVisibility.value = !pwdVisibility.value;
};

const route = useRoute();
const redirectUrl = (route.query.redirect as string) || "/dashboard";
const goBackUrl = (route.query.back as string) || "/";

const useDemoAccount = async () => {
  email.value = "user@demo.com";
  password.value = "password";
  name.value = "Demo User";
};

const handleRegister = async () => {
  const response = await authStore.register({
    email: email.value,
    password: password.value,
    name: name.value,
  });
  if (response && !response.success) {
    error.value = String(response.error) || "An error occurred";
  } else if (response && response.success) {
    error.value = "";
    await nextTick(); // Wait for the DOM to update
    return await navigateTo(redirectUrl, { replace: true });
  } else {
    error.value = "An error occurred";
  }
};
</script>

<template>
  <div
    class="w-full max-w-[400px] mx-auto p-4 rounded-md text-gray-500 dark:text-zinc-300 bg-gray-50 dark:bg-zinc-900"
  >
    <h2 class="text-2xl font-semibold text-center capitalize">
      create an account
    </h2>

    <p
      v-if="!authStore.isAuthenticated"
      title="Click to use our demo account"
      class="flex flex-col mt-4 font-semibold items-center space-x-4 my-1.5 border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 py-1 px-1.5 rounded-md text-gray-400 text-xs cursor-pointer mx-auto"
      @click="useDemoAccount"
    >
      <span class="py-1 px-2.5 uppercase">Use Demo Account</span>
    </p>

    <p
      v-if="error"
      class="text-red-400 bg-red-50 p-1.5 px-3 rounded-md my-2 border border-red-100"
    >
      {{ error }}
    </p>

    <form class="mt-4 flex flex-col gap-y-2.5" @submit.prevent="handleRegister">
      <div>
        <UInput
          v-model="name"
          type="text"
          icon="i-lucide-user-round"
          size="lg"
          class="w-full rounded-md outline-none border border-gray-300 dark:border-gray-700"
          variant="none"
          color="neutral"
          placeholder="Your Full Name"
        />
      </div>
      <div>
        <UInput
          v-model="email"
          type="email"
          icon="i-lucide-mail"
          size="lg"
          class="w-full rounded-md outline-none border border-gray-300 dark:border-gray-700"
          variant="none"
          color="neutral"
          placeholder="Your Email Address"
        />
      </div>
      <div>
        <UInput
          v-model="password"
          :type="pwdVisibility ? 'text' : 'password'"
          icon="i-lucide-lock"
          size="lg"
          class="w-full rounded-md outline-none border border-gray-300 dark:border-gray-700 text-gray-500 dark:text-gray-600"
          variant="none"
          :ui="{ trailing: 'pe-1' }"
          color="neutral"
          placeholder="Your Password (i.e. **********)"
        >
          <template #trailing>
            <UButton
              color="neutral"
              class="text-gray-500 bg-inherit"
              :icon="pwdVisibility ? 'i-lucide-eye-off' : 'i-lucide-eye'"
              :aria-label="pwdVisibility ? 'Hide password' : 'Show password'"
              :aria-pressed="pwdVisibility"
              aria-controls="password"
              @click="togglePwdVisibility"
            /> </template
        ></UInput>
      </div>
      <div>
        <button
          type="submit"
          class="w-full bg-emerald-500 border border-emerald-300 hover:bg-emerald-600 hover:border-emerald-400 transition-colors text-white rounded-md p-2 font-semibold"
        >
          Register
        </button>
      </div>

      <div class="mt-4 text-center">
        Already have an account?
        <span class="mx-1">|</span>
        <NuxtLink to="/auth/login" class="text-emerald-600">Login</NuxtLink>
      </div>

      <div class="text-center">
        <NuxtLink :to="goBackUrl" class="text-zinc-400">Back to Home</NuxtLink>
      </div>
    </form>
  </div>
</template>
